/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Stack } from '@fluentui/react/lib/Stack';
import { ITextFieldStyleProps, ITextFieldStyles } from '@fluentui/react/lib/TextField';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { LuisTextField } from 'src/core/components/LuisTextField';
import { useLocalization } from 'src/core/hooks/useLocalization';

export type SublistNormalizedValueCellProps = {
    /**
     * The sublist normalized value.
     */
    normalizedValue: string;

    /**
     * Callback for submitting a new normalized value.
     */
    onChange: (value: string) => void;

    /**
     * Extra styles
     */
    className?: string;

    /**
     * Determines whether the textfield is disabled
     */
    isDisabled?: boolean;
};

const messages = defineMessages({
    fieldPlaceholder: {
        id: 'listEntityDetails.sublistNormalizedValueCell.fieldPlaceholder',
        defaultMessage: 'Type in a list item ...'
    },

    disabledFieldPlaceholder: {
        id: 'listEntityDetails.sublistNormalizedValueCell.disabledFieldPlaceholder',
        defaultMessage: 'Your current role cannot add new items'
    }
});

const getClassName = classNamesFunction<ITextFieldStyleProps, ITextFieldStyles>();

const fabricTransparentFieldClassName = getClassName({
    field: { padding: 0, backgroundColor: 'transparent' },
    fieldGroup: { backgroundColor: 'transparent' }
});

/**
 * Renders, edit and add normalized values for
 * the sublist table for the list entity
 * details in the entity details view.
 *
 * @param props The input properties for the cell.
 */
export const SublistNormalizedValueCell = (props: SublistNormalizedValueCellProps) => {
    const { normalizedValue, onChange, className, isDisabled } = props;

    const { localize } = useLocalization();
    const [newNormalizedValue, setNewNormalizedValue] = React.useState(normalizedValue);

    /**
     * submits the current value if exists to the parent component.
     */
    const onSubmit = (): void => {
        if (newNormalizedValue && newNormalizedValue !== normalizedValue) {
            onChange(newNormalizedValue);
        } else {
            setNewNormalizedValue(normalizedValue);
        }

        /**
         * If the component starts with an empty normalizedValue
         * then, it's used for adding a new value and needs refreshing.
         */
        if (!normalizedValue) {
            setNewNormalizedValue('');
        }
    };

    return (
        <Stack verticalAlign="center" verticalFill className={className}>
            <LuisTextField
                borderless
                styles={fabricTransparentFieldClassName}
                placeholder={isDisabled ? localize(messages.disabledFieldPlaceholder) : localize(messages.fieldPlaceholder)}
                title={isDisabled ? localize(messages.disabledFieldPlaceholder) : localize(messages.fieldPlaceholder)}
                ariaLabel={localize(messages.fieldPlaceholder)}
                value={newNormalizedValue}
                onChange={(_, value) => setNewNormalizedValue(value)}
                onBlur={onSubmit}
                onEnterKeyUp={onSubmit}
                disabled={isDisabled}
            />
        </Stack>
    );
};
